




















import { Component, Prop, Vue } from 'vue-property-decorator';
import { VatsApi } from '@/api';
import moment from 'moment-timezone';
import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { ApiUtils } from '@/common/ApiUtils';
import { TooltipLabelBuilder } from '@/pages/account/products/feed/util/tooltip-label-builder';
import { Bullet, Circle, Color, Label, Legend, percent, Root, Tooltip } from '@amcharts/amcharts5';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import { VatModels } from '@/common/EmptyDistanceUtils';
import { Vat } from '@/api';

interface CollectionPoint {
	distance_mm: number;
	levno_volume: number;
	supplier_volume: number;
	supplier_timestamp: number;
	supplier_date: string;
	opacity: number;
}

@Component({
	components: { DatePicker },
})
export default class VatCalibrationScatter extends Vue {
	@Prop({ required: true, type: Object })
	public vat!: Vat;

	public loading: boolean = true;
	public errorMessage: string | null = null;
	public chartData: CollectionPoint[] = [];

	// Use 90-day date range by default. No real reason, felt like a good time span
	private startDate: moment.Moment = moment().startOf('day').subtract(90, 'days');
	private endDate: moment.Moment = moment().startOf('day').add(1, 'days');

	private vatsApi: VatsApi = new VatsApi();
	private root: Root | null = null;

	public get chartId(): string {
		return `vatCalibrationScatter-${this.vat.id}`;
	}

	public async created(): Promise<void> {
		await this.fetchData();
		this.applyOpacity();
		if (this.chartData.length > 0) {
			this.$nextTick(() => this.createChart());
		}
	}

	public beforeDestroy(): void {
		this.root?.dispose();
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		if (!newDateRange.end) return;
		this.startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		this.endDate = moment(newDateRange.end, 'DD-MM-YYYY');
		await this.fetchData();
		this.applyOpacity();
		if (this.chartData.length > 0) {
			this.$nextTick(() => this.createChart());
		} else {
			this.root?.dispose();
			this.root = null;
		}
	}

	private async fetchData(): Promise<void> {
		this.loading = true;
		this.errorMessage = null;
		try {
			const response = await this.vatsApi.getVatCollectionsVsDistance(
				this.vat.id,
				this.startDate.unix(),
				this.endDate.unix(),
			);
			this.chartData = (response.data as CollectionPoint[])
				.filter(d => d.distance_mm != null && d.levno_volume != null && d.supplier_volume != null)
				.sort((a, b) => b.supplier_timestamp - a.supplier_timestamp)
				.map(d => ({
					...d,
					supplier_date: moment.unix(d.supplier_timestamp).tz('Pacific/Auckland').format('DD MMM YYYY HH:mm'),
				}));
		} catch (e) {
			const { errorMessage } = ApiUtils.handleApiError(e, 'Failed to load collection data');
			this.errorMessage = errorMessage;
			this.chartData = [];
		} finally {
			this.loading = false;
		}
	}

	private applyOpacity(): void {
		const minOpacity = 0.1;
		const step = (1 - minOpacity) / (this.chartData.length - 1 || 1);
		this.chartData = this.chartData.map((d, i) => ({ ...d, opacity: 1 - step * i }));
	}

	private makeSeries(
		root: Root,
		xAxis: ValueAxis<AxisRendererX>,
		yAxis: ValueAxis<AxisRendererY>,
		name: string,
		valueYField: string,
		color: Color,
		tooltipHeader: string,
	): LineSeries {
		const tooltip = Tooltip.new(root, {
			getFillFromSprite: false,
			labelText: new TooltipLabelBuilder()
				.line(tooltipHeader, { fontSize: 12 })
				.line('{valueY} L at {valueX} mm', { fontSize: 14, bold: true })
				.line('{supplier_date}', { color: '#4a4a4a', fontSize: 10 })
				.build(),
		});
		tooltip.get('background')?.setAll({ fill: color, fillOpacity: 0.8, strokeOpacity: 0 });

		const series = LineSeries.new(root, {
			name,
			xAxis,
			yAxis,
			valueXField: 'distance_mm',
			valueYField,
			stroke: color,
			fill: color,
			tooltip,
		});
		series.strokes.template.setAll({ strokeOpacity: 0 });
		series.bullets.push((r, _s, dataItem) =>
			Bullet.new(r, {
				sprite: Circle.new(r, {
					radius: 5,
					fill: color,
					fillOpacity: (dataItem.dataContext as CollectionPoint).opacity,
					stroke: color,
					strokeOpacity: 1,
					strokeWidth: 1,
				}),
			})
		);
		return series;
	}

	private getModelAttributes() {
		if (!this.vat.formula || !this.vat.sizeString) return null;
		const attrs = VatModels.get(`${this.vat.formula}:${this.vat.sizeString}`);
		if (!attrs) return null;
		const mmToMetres = (mm: number) => mm / 1000;
		return {
			slopeOffset: mmToMetres(attrs.slopeOffset),
			diameter: mmToMetres(attrs.diameter),
			height: mmToMetres(this.vat.height ?? attrs.height),
			ventDistance: mmToMetres(this.vat.ventDistance ?? attrs.ventDistance),
			offset: mmToMetres(this.vat.vatOffset ?? attrs.defaultOffset ?? 0),
		};
	}

	private calculateClassicFormulaVolumes(
		xMax: number,
		params: { slopeOffset: number; diameter: number; height: number; ventDistance: number; offset: number },
	): { distance_mm: number; model_volume: number }[] {
		const { slopeOffset, diameter, height, ventDistance, offset } = params;
		const angle = Math.atan(slopeOffset / diameter);
		const r = diameter / 2;
		const calcVolume = (distanceMm: number) => {
			const readingM = distanceMm / 1000;
			const Hm1 = height + offset - readingM + ventDistance * Math.tan(angle);
			const Hm2 = Math.max(0, Hm1 - slopeOffset);
			return Math.round(Math.PI * r * r * ((Hm1 + Hm2) / 2) * 1000);
		};
		const slopeTransitionMm = (height + offset + ventDistance * Math.tan(angle) - slopeOffset) * 1000;
		const points = [
			{ distance_mm: 0, model_volume: calcVolume(0) },
			{ distance_mm: slopeTransitionMm, model_volume: calcVolume(slopeTransitionMm) },
			{ distance_mm: xMax, model_volume: calcVolume(xMax) },
		];
		return points;
	}

	private createChart(): void {
		this.root?.dispose();
		this.root = Root.new(this.chartId);
		const chart = XYChart.new(this.root, {});

		const dataMaxDistance = Math.max(...this.chartData.map(d => d.distance_mm));
		const dataMaxVolume = Math.max(...this.chartData.map(d => d.supplier_volume));
		const xMax = this.vat.emptyDistance != null ? Math.max(this.vat.emptyDistance, dataMaxDistance) : dataMaxDistance;
		const parsedSize = this.vat.sizeString != null ? parseInt(this.vat.sizeString) : NaN;
		
		const modelParams = this.getModelAttributes();
		const modelData = modelParams ? this.calculateClassicFormulaVolumes(xMax, modelParams) : [];
		
		const yMax = !isNaN(parsedSize) ? Math.max(parsedSize, dataMaxVolume) : dataMaxVolume;

		const xAxis = ValueAxis.new(this.root, {
			renderer: AxisRendererX.new(this.root, {}),
			tooltip: Tooltip.new(this.root, {}),
			min: 0,
			max: xMax,
		});

		const yAxis = ValueAxis.new(this.root, {
			renderer: AxisRendererY.new(this.root, {}),
			tooltip: Tooltip.new(this.root, {}),
			min: 0,
			max: yMax,
		});

		xAxis.get('renderer').labels.template.setAll({ fontFamily: 'sofia-pro-soft' });
		yAxis.get('renderer').labels.template.setAll({ fontFamily: 'sofia-pro-soft' });

		xAxis.children.push(Label.new(this.root, {
			text: 'Sensor Distance (mm)',
			x: percent(50),
			centerX: percent(50),
			fontFamily: 'sofia-pro-soft',
		}));

		yAxis.children.moveValue(Label.new(this.root, {
			text: 'Volume (L)',
			y: percent(50),
			centerX: percent(50),
			rotation: -90,
			fontFamily: 'sofia-pro-soft',
		}), 0);

		const levnoSeries = this.makeSeries(this.root, xAxis, yAxis, 'Levno Volume (L)', 'levno_volume', Color.fromHex(0xff7a00), 'Levno Volume');
		const supplierSeries = this.makeSeries(this.root, xAxis, yAxis, 'Supplier Volume (L)', 'supplier_volume', Color.fromHex(0x2979ff), 'Supplier Volume');

		const legend = Legend.new(this.root, {
			x: percent(50),
			centerX: percent(50),
		});

		const cursor = XYCursor.new(this.root, {});

		chart.set('cursor', cursor);
		chart.xAxes.push(xAxis);
		chart.yAxes.push(yAxis);
		chart.series.push(levnoSeries);
		chart.series.push(supplierSeries);

		if (modelData.length > 0) {
			const modelSeries = LineSeries.new(this.root, {
				name: 'Classic Formula Volume',
				xAxis,
				yAxis,
				valueXField: 'distance_mm',
				valueYField: 'model_volume',
				stroke: Color.fromHex(0x2ca02c),
				fill: Color.fromHex(0x2ca02c),
			});
			modelSeries.strokes.template.setAll({ strokeWidth: 2, strokeOpacity: 1 });
			chart.series.push(modelSeries);
			modelSeries.data.setAll(modelData);
		}

		chart.bottomAxesContainer.children.push(legend);

		levnoSeries.data.setAll(this.chartData);
		supplierSeries.data.setAll(this.chartData);
		legend.data.setAll(chart.series.values);

		this.root.container.children.push(chart);
		chart.appear(1000, 100);
	}
}
